import React, {
  useState,
  createRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import { connect } from 'react-redux'
import ChargePointSelectors from 'Stores/ChargePoint/Selectors'
import OCPPServices from 'Services/OCPPService'
import {
  IFText,
  ConnectorListItem,
  ConnectorListItemSkeleton,
  ReserveConnector,
  CancelConnectorReservation,
} from 'Components'
import { Icons } from 'Theme'
import Styles from './ConnectorList.module.css'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import ReactTimeAgo from 'react-time-ago'
import StationSelectors from 'Stores/Station/Selectors'
import StartTx from 'Components/StartTx/StartTx'
import InfinityEnums from 'Enums/InfinityEnums'
import { getConnectorDisplayStatus } from 'Utils/ConnectorFunctions'
import AuthSelectors from 'Stores/Auth/Selectors'
import ChargePointActions from 'Stores/ChargePoint/Actions'
import moment from 'moment'
import { Emitter } from '../../Services/EmitterService'
import OperatorUserSelectors from 'Stores/OperatorUser/Selectors'
import OperatorUserActions from 'Stores/OperatorUser/Actions'
import TariffSelectors from 'Stores/Tariff/Selectors'

const ConnectorList = React.forwardRef(
  (
    {
      connectorList,
      isLoading = false,
      fetchChargePointListRequestState,
      fetchStationListRequestState,
      isEdit,
      onMaxPowerChange,
      onTypeChange,
      chargePointUid,
      isChargePointOffline,
      chargePoint,
      operatorUser,
      emitterUpdateConnectorSessionStatus,
      fetchChargePointRequestState,
      onStatusChange = () => {},
      onTariffChange = () => {},
      operator,
      fetchConnectorTypes,
      fetchConnectorTypesRequestState,
      connectorTypes,
      fetchChargingSessionEvents,
      fetchChargingSessionDetails,
      tariffsList,
      fetchTariffsList,
      onVisibleChange = () => {},
      fetchTariffListRequestState,
      onSocLimitChange = () => {},
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const NumberOfActions = 4
    const connectorsLength = connectorList.length
    const connectorListItemRefs = React.useRef([])
    const confirmStartTxRefs = React.useRef([])
    const reserveConnectorRef = React.useRef()
    const cancelReserveConnectorRef = React.useRef()
    const [previousChargePoint, setPreviousChargePoint] = useState(null)
    if (confirmStartTxRefs.length !== connectorsLength) {
      confirmStartTxRefs.current = Array(connectorsLength)
        .fill()
        .map((_, i) => confirmStartTxRefs.current[i] || createRef())
    }

    if (connectorListItemRefs.current.length !== connectorsLength) {
      connectorListItemRefs.current = Array(connectorsLength)
        .fill()
        .map((_, i) => connectorListItemRefs.current[i] || createRef())
    }

    useImperativeHandle(ref, () => ({
      resetMaxPowerValues,
      closeAccordions,
      resetStatusValue,
      resetVisibilityValue,
      resetSocLimitValues,
    }))
    const [loadingStates, setLoadingStates] = useState(
      new Array(connectorList.length * NumberOfActions).fill(false),
    )

    const resetStatusValue = () => {
      connectorListItemRefs.current.map((connectorListItemRef) => {
        if (connectorListItemRef && connectorListItemRef.current !== null) {
          connectorListItemRef.current.resetStatusValue()
        }
      })
    }

    const resetVisibilityValue = () => {
      connectorListItemRefs.current.map((connectorListItemRef) => {
        if (connectorListItemRef && connectorListItemRef.current !== null) {
          connectorListItemRef.current.resetVisibilityValue()
        }
      })
    }

    const resetSocLimitValues = () => {
      connectorListItemRefs.current.map((connectorListItemRef) => {
        if (connectorListItemRef && connectorListItemRef.current !== null) {
          connectorListItemRef.current.resetSocLimitValue()
        }
      })
    }

    const handleLoadingStates = (index, offset, value) => {
      setLoadingStates((prevState) => {
        prevState[NumberOfActions * index + offset] = value
        return [...prevState]
      })
    }

    const resetMaxPowerValues = () => {
      connectorListItemRefs.current.map((connectorListItemRef) => {
        if (connectorListItemRef && connectorListItemRef.current !== null) {
          connectorListItemRef.current.resetMaxPowerValue()
        }
      })
    }

    const closeAccordions = () => {
      connectorListItemRefs.current.map((connectorListItemRef) => {
        if (connectorListItemRef && connectorListItemRef.current !== null) {
          connectorListItemRef.current.closeAccordion()
        }
      })
    }
    const dismiss = (index, isError) => {
      if (isError) confirmStartTxRefs.current[index].current.handleError()
      else confirmStartTxRefs.current[index].current.dismiss()
    }
    const dismissReserveConnector = (isError) => {
      if (isError) reserveConnectorRef.current.handleError()
      else reserveConnectorRef.current.dismiss()
    }
    const dismissCancelReserveConnectorRef = () => {
      cancelReserveConnectorRef.current.dismiss()
    }
    useEffect(() => {
      if (previousChargePoint) {
        if (chargePoint) {
          if (previousChargePoint._id !== chargePoint._id) {
            Emitter.unsubscribeFromChannelGroup(
              `operator/${operator._id}/chargePoint/${previousChargePoint.huid}/connector/`,
              operator.channelKey,
            )
          }
        } else {
          Emitter.unsubscribeFromChannelGroup(
            `operator/${operator._id}/chargePoint/${previousChargePoint.huid}/connector/`,
            operator.channelKey,
          )
        }
      }
      if (chargePoint) {
        setPreviousChargePoint(chargePoint)
        chargePoint.evses?.forEach((evse) => {
          evse.connectors?.forEach((connector) => {
            if (connector.uid === 0) return
            Emitter.subscribeToChannel(
              `operator/${operator._id}/chargePoint/${chargePoint.huid}/connector/${connector.uid}/sessionStatus/`,
              operator.channelKey,
              emitterUpdateConnectorSessionStatus,
              {},
            )
          })
        })
      }
    }, [chargePoint])

    useEffect(() => {
      if (chargePoint) {
        fetchConnectorTypes()
        fetchTariffsList()
      }
    }, [chargePoint])
    return (
      <div className={Styles.ConnectorsWrapper}>
        {fetchChargePointListRequestState === RequestState.LOADING ||
        fetchStationListRequestState === RequestState.LOADING ||
        fetchChargePointRequestState === RequestState.LOADING ||
        fetchTariffListRequestState === RequestState.LOADING ||
        isLoading ? (
          [...Array(2)].map((e, index) => (
            <div
              key={index}
              style={{
                marginBottom: '0.5rem',
              }}
            >
              <ConnectorListItemSkeleton
                editPermission={operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_EDIT_CHARGING,
                )}
              />
            </div>
          ))
        ) : connectorList.length === 0 ? (
          <div className={Styles.EmptySet}>
            <img src={Icons.emptySet.default} alt="" />
            <IFText>{t('ConnectorList.Empty')}</IFText>
          </div>
        ) : (
          connectorList
            .filter((connector) => {
              return connector.uid !== 0
            })
            .map((connector, index) => (
              <div
                key={index}
                style={{
                  marginBottom:
                    index === connectorList.length - 1 ? '0rem' : '0.5rem',
                }}
              >
                <StartTx
                  ref={confirmStartTxRefs.current[index]}
                  onStartClick={(input) => {
                    OCPPServices.startTransaction(
                      chargePoint._id,
                      connector.uid,
                      input,
                      (isError) => {
                        dismiss(index, isError)
                      },
                    )
                  }}
                />
                <ConnectorListItem
                  key={'ConnectorListItem' + index + '_' + connector.uid}
                  ref={connectorListItemRefs.current[index]}
                  isLoading={isLoading}
                  isRemoved={connector.isRemoved}
                  chargePoint={chargePoint}
                  hasOcpiIntegration={operator.hasOcpiIntegration}
                  maxPower={connector.maxPower}
                  status={connector.status}
                  type={connector.type}
                  isChargePointOffline={isChargePointOffline}
                  uid={connector.uid}
                  meterValue={connector.meterValue}
                  session={connector.session}
                  reservation={connector.reservation}
                  lastUpdateTime={
                    <ReactTimeAgo
                      date={new Date(connector.statusUpdatedAt)}
                      locale="en-US"
                      timeStyle="mini"
                    />
                  }
                  onStartClick={() => {
                    confirmStartTxRefs.current[index].current.show()
                    handleLoadingStates(index, 0, true)
                  }}
                  onStopClick={() => {
                    // TODO: charging token and charging session ?
                    handleLoadingStates(index, 1, true)
                    OCPPServices.stopTransaction(
                      chargePoint._id,
                      connector.uid,
                      () => {
                        handleLoadingStates(index, 1, false)
                      },
                    )
                  }}
                  onUnlockClick={() => {
                    handleLoadingStates(index, 2, true)
                    OCPPServices.unlockConnector(
                      chargePoint._id,
                      connector.uid,
                      () => {
                        handleLoadingStates(index, 2, false)
                      },
                    )
                  }}
                  isEdit={isEdit}
                  onMaxPowerChange={(value) =>
                    onMaxPowerChange(value, connector._id)
                  }
                  onStatusChange={(value) =>
                    onStatusChange(value, connector._id)
                  }
                  onTariffChange={(value) => {
                    let tariff = tariffsList.find(
                      (tarr) => tarr._id === value.value,
                    )
                    onTariffChange(tariff, connector._id)
                  }}
                  onTypeChange={(value) => onTypeChange(value, connector._id)}
                  onVisibleChange={(value) => {
                    onVisibleChange(value, connector._id)
                  }}
                  powerType={connector.powerType}
                  socLimit={connector.socLimit}
                  onSocLimitChange={(value) =>
                    onSocLimitChange(value, connector._id)
                  }
                  isStopTxLoading={loadingStates[NumberOfActions * index + 1]}
                  isUnlockLoading={loadingStates[NumberOfActions * index + 2]}
                  isStartTxDead={
                    // Startable when idle (Available) or plugged-in-pre-charge.
                    // Post status-model inversion a plugged-but-not-yet-charging
                    // connector reads the coarse `Occupied` (its session has no
                    // fine chargingState yet). Fine energy states resolve from
                    // the active session via getConnectorDisplayStatus.
                    getConnectorDisplayStatus(connector) !==
                      InfinityEnums.ConnectorStatus.AVAILABLE &&
                    getConnectorDisplayStatus(connector) !==
                      InfinityEnums.ConnectorStatus.OCCUPIED
                  }
                  isStopTxDead={
                    // Stoppable only while a transaction is drawing/suspended.
                    // These fine states now live on the connector's active
                    // session (session.detailedStatus), not on connector.status.
                    !(
                      getConnectorDisplayStatus(connector) ===
                        InfinityEnums.ChargingSessionChargingState.CHARGING ||
                      getConnectorDisplayStatus(connector) ===
                        InfinityEnums.ChargingSessionChargingState.SUSPENDEDEV ||
                      getConnectorDisplayStatus(connector) ===
                        InfinityEnums.ChargingSessionChargingState.SUSPENDEDEVSE
                    )
                  }
                  editPermission={operatorUser.permissions.includes(
                    InfinityEnums.OperatorUserPermission.CAN_EDIT_CHARGING,
                  )}
                  onSyncClick={() => {
                    handleLoadingStates(index, 3, true)
                    OCPPServices.syncConnectorStatus(
                      chargePoint._id,
                      connector.uid,
                      () => {
                        handleLoadingStates(index, 3, false)
                      },
                    )
                  }}
                  isSyncLoading={loadingStates[NumberOfActions * index + 3]}
                  onReserveClick={() => {
                    reserveConnectorRef.current.show(connector.uid)
                  }}
                  onCancelReserveClick={() => {
                    cancelReserveConnectorRef.current.show(connector.uid)
                  }}
                  operatorUser={operatorUser}
                  errorCode={connector.errorCode}
                  vendorErrorCode={connector.vendorErrorCode}
                  vendorId={connector.vendorId}
                  fetchConnectorTypesRequestState={
                    fetchConnectorTypesRequestState === RequestState.LOADING
                  }
                  connectorTypes={connectorTypes}
                  handleOpenChargingEventsModal={() => {
                    fetchChargingSessionEvents(connector.session._id)
                    fetchChargingSessionDetails(connector.session._id)
                  }}
                  connectorTariff={connector.tariffIds?.[0]}
                  tariffsList={tariffsList}
                  currency={operator.country.currency}
                />
              </div>
            ))
        )}
        <ReserveConnector
          ref={reserveConnectorRef}
          onClick={(chargingTokenUid, reserveTime, connectorUid) => {
            let reserveInfo = reserveTime.split(' ')
            let expiresAt = moment(Date.now()).add(
              Number(reserveInfo[0]),
              reserveInfo[1],
            )
            OCPPServices.reserveConnector(
              chargePoint._id,
              connectorUid,
              chargingTokenUid,
              expiresAt,
              (isError) => {
                dismissReserveConnector(isError)
              },
            )
          }}
        />
        <CancelConnectorReservation
          ref={cancelReserveConnectorRef}
          onClick={(connectorUid) => {
            const connector = connectorList.find((c) => c.uid === connectorUid)
            OCPPServices.CancelConnectorReservation(
              connector.reservation._id,
              () => {
                dismissCancelReserveConnectorRef()
              },
            )
          }}
        />
      </div>
    )
  },
)

const mapStateToProps = (state) => ({
  connectorList: ChargePointSelectors.getChargePointConnectors(state),
  fetchChargePointListRequestState:
    ChargePointSelectors.getFetchChargePointListRequestState(state),
  fetchChargePointRequestState:
    ChargePointSelectors.getFetchChargePointRequestState(state),
  chargePointUid: ChargePointSelectors.getChargePointUid(state),
  chargePoint: ChargePointSelectors.getSelectedChargePoint(state),
  fetchStationListRequestState:
    StationSelectors.getFetchStationListRequestState(state),
  operatorUser: AuthSelectors.getOperatorUser(state),
  operator: AuthSelectors.getOperator(state),
  fetchConnectorTypesRequestState:
    ChargePointSelectors.getFetchConnectorTypesRequestState(state),
  connectorTypes: ChargePointSelectors.getConnectorTypes(state),
  tariffsList: OperatorUserSelectors.getTariffsList(state),
  fetchTariffListRequestState:
    OperatorUserSelectors.getFetchTariffListRequestState(state),
})
function mapDispatchToProps(dispatch) {
  return {
    emitterUpdateConnectorSessionStatus: (args) =>
      dispatch(ChargePointActions.emitterUpdateConnectorSessionStatus(args)),
    fetchConnectorTypes: () =>
      dispatch(ChargePointActions.fetchConnectorTypes()),
    fetchChargingSessionEvents: (chargingSessionId) =>
      dispatch(
        ChargePointActions.fetchChargingSessionEvents(chargingSessionId),
      ),
    fetchChargingSessionDetails: (chargingSessionId) =>
      dispatch(
        ChargePointActions.fetchChargingSessionDetails(chargingSessionId),
      ),
    fetchTariffsList: () => dispatch(OperatorUserActions.fetchTariffsList()),
  }
}

ConnectorList.propTypes = {
  connectorList: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  fetchChargePointListRequestState: PropTypes.number,
  fetchChargePointRequestState: PropTypes.number,
  isEdit: PropTypes.bool,
  onMaxPowerChange: PropTypes.func,
  onTypeChange: PropTypes.func,
  chargePointUid: PropTypes.string,
  operatorUser: PropTypes.object,
  emitterUpdateConnectorSessionStatus: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(ConnectorList)
